import { Component, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  ActionButtonVm,
  GridColumnVm,
  GridContentVm,
  GridRowVm,
  PolicyExplorerResponse,
  RecordFieldVm,
  TreeNodeVm
} from './policy-explorer.models';
import { PolicyExplorerService } from './policy-explorer-service';
import { LoginService } from '../../../services/login-service';

interface RecordDraftState {
  nodeKey: string;
  recordId?: string;
  title?: string;
  originalValues: Record<string, any>;
  editedValues: Record<string, any>;
}

interface CalculatedRecordState {
  values: Record<string, any>;
  formattedValues: Record<string, string | null>;
}
@Component({
  selector: 'wc-policy-explorer',
  standalone: false,
  templateUrl: './policy-explorer.component.html',
  styleUrls: ['./policy-explorer.component.css']
})
export class PolicyExplorerComponent implements OnChanges {
  @Input({ required: true }) clientId!: string;
  @Input({ required: true }) schemaName!: string;
  @Input({ required: true }) tableName!: string;
  @Input({ required: true }) recordId!: string;
  @Input() dataSetId?: string | null;

  loading = false;
  error?: string;
  vm?: PolicyExplorerResponse;

  page = 1;
  pageSize = 25;
  search = '';
  sortField?: string;
  sortDirection: 'asc' | 'desc' = 'asc';

  editMode = false;

  originalValues: Record<string, any> = {};
  editedValues: Record<string, any> = {};
  activeTab: 'attachments' | null = null;
  activeSection: string | null = null;
  private draftsByNodeKey: Record<string, RecordDraftState> = {};

  constructor(public loginService: LoginService,
    private readonly service: PolicyExplorerService) { }

  exportMenuOpen = false;

  toggleExportMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.exportMenuOpen = !this.exportMenuOpen;
  }

  closeExportMenu(): void {
    this.exportMenuOpen = false;
  }

  exportAs(format: 'csv' | 'json' | 'xml', event?: MouseEvent): void {
    event?.stopPropagation();
    this.closeExportMenu();

    this.service.export(
      format,
      this.clientId,
      this.schemaName,
      this.tableName,
      this.recordId,
      this.dataSetId
    );
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeExportMenu();
  }

  get visibleButtons(): ActionButtonVm[] {
    if (!this.vm) return [];
    return this.vm.toolbar.buttons.filter(x => x.visible !== false && x.enabled);
  }

  private calculatedValuesByNodeKey: Record<string, CalculatedRecordState> = {};
  private calculatedGridValuesByNodeKey: Record<string, Record<string, CalculatedRecordState>> = {};

  private applyLocalStateToCurrentRecord(): void {
    if (!this.vm || !this.isRecordKind(this.vm.content?.kind) || !this.vm.content.record) {
      return;
    }

    const nodeKey = this.vm.selectedNodeKey;
    const calcState = this.calculatedValuesByNodeKey[nodeKey];

    for (const field of this.vm.content.record.fields) {
      if (field.isCalculated && calcState?.values && Object.prototype.hasOwnProperty.call(calcState.values, field.fieldName)) {
        field.value = calcState.values[field.fieldName];
        field.formattedValue = calcState.formattedValues?.[field.fieldName] ?? String(calcState.values[field.fieldName] ?? '');
        continue;
      }

      if (!field.isCalculated && Object.prototype.hasOwnProperty.call(this.editedValues, field.fieldName)) {
        field.value = this.editedValues[field.fieldName];
        field.formattedValue = this.editedValues[field.fieldName] ?? '';
      }
    }
  }

  private applyLocalStateToCurrentGrid(): void {
    if (!this.vm || !this.isGridKind(this.vm.content?.kind) || !this.vm.content.grid) {
      return;
    }

    const nodeKey = this.vm.selectedNodeKey;
    const calcRows = this.calculatedGridValuesByNodeKey[nodeKey] || {};

    for (const row of this.vm.content.grid.rows) {
      const rowCalc = calcRows[row.id];
      if (!rowCalc) continue;

      for (const key of Object.keys(rowCalc.values || {})) {
        row.values[key] = rowCalc.values[key];
        row.formattedValues[key] = rowCalc.formattedValues?.[key] ?? String(rowCalc.values[key] ?? '');
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId']) {
      console.log('ClientId changed:', this.clientId);
    }
    if (
      changes['clientId'] ||
      changes['schemaName'] ||
      changes['tableName'] ||
      changes['recordId'] ||
      changes['dataSetId']
    ) {
      this.resetExplorerState();
      this.load();
    }
  }

  private resetExplorerState(): void {
    this.loading = false;
    this.error = undefined;
    this.vm = undefined;

    this.page = 1;
    this.pageSize = 25;
    this.search = '';
    this.sortField = undefined;
    this.sortDirection = 'asc';

    this.editMode = false;
    this.originalValues = {};
    this.editedValues = {};
    this.draftsByNodeKey = {};
    this.exportMenuOpen = false;
  }

  private currentNodeKey(): string | undefined {
    return this.vm?.selectedNodeKey;
  }

  private currentRecordFields(): RecordFieldVm[] {
    return this.vm?.content?.record?.fields ?? [];
  }

  private buildServerValuesFromVm(): Record<string, any> {
    const values: Record<string, any> = {};
    for (const field of this.currentRecordFields()) {
      values[field.fieldName] = field.value;
    }
    return values;
  }

  load(selectedNodeKey?: string): void {
    if (!this.clientId || !this.schemaName || !this.tableName || !this.recordId) {
      return;
    }

    this.loading = true;
    this.error = undefined;
    this.closeExportMenu();
    this.service.open(
      this.clientId,
      this.schemaName,
      this.tableName,
      this.recordId,
      (vm: PolicyExplorerResponse) => {
        this.vm = this.normalizeVm(vm);
        this.hydrateDraftFromVm();
        this.loading = false;
        if (this.vm?.content?.record) {
          console.log('Loaded Record:', this.vm.content.record);
        } else {
          console.log('Loaded Grid or empty content');
        }
      },
      this.dataSetId,
      selectedNodeKey ?? this.vm?.selectedNodeKey ?? null,
      this.page,
      this.pageSize,
      this.sortField,
      this.sortDirection,
      this.search || null,
      this.editMode
    );
  }

  private normalizeVm(vm: PolicyExplorerResponse): PolicyExplorerResponse {
    const kind = vm.content?.kind as any;
    if (kind === 'Record' || kind === '1') vm.content.kind = 1 as any;
    if (kind === 'Grid' || kind === '2') vm.content.kind = 2 as any;
    return vm;
  }

  private hydrateDraftFromVm(): void {
    if (!this.vm || !this.isRecordKind(this.vm.content?.kind)) {
      this.originalValues = {};
      this.editedValues = {};
      return;
    }

    const nodeKey = this.vm.selectedNodeKey;
    const serverValues = this.buildServerValuesFromVm();
    const existingDraft = this.draftsByNodeKey[nodeKey];

    if (!existingDraft) {
      this.draftsByNodeKey[nodeKey] = {
        nodeKey,
        recordId: this.vm.rootRecordId,
        title: this.vm.content.record?.title,
        originalValues: { ...serverValues },
        editedValues: { ...serverValues }
      };
    } else {
      // Preserve local edits, but do not clobber original values.
      const mergedEditedValues = {
        ...serverValues,
        ...existingDraft.editedValues
      };

      this.draftsByNodeKey[nodeKey] = {
        ...existingDraft,
        title: this.vm.content.record?.title ?? existingDraft.title,
        editedValues: mergedEditedValues
      };
    }

    this.originalValues = { ...this.draftsByNodeKey[nodeKey].originalValues };
    this.editedValues = { ...this.draftsByNodeKey[nodeKey].editedValues };
    this.applyLocalStateToCurrentRecord();
    this.applyLocalStateToCurrentGrid();
  }

  calculateCurrentRecord(): void {
    if (!this.vm || !this.isRecordKind(this.vm.content?.kind)) {
      return;
    }

    this.service.calculateRecord(
      this.clientId,
      this.schemaName,
      this.tableName,
      this.recordId,
      {
        dataSetId: this.dataSetId,
        selectedNodeKey: this.vm.selectedNodeKey
      },
      (result: any) => {
        const nodeKey = result.nodeKey || this.vm!.selectedNodeKey;

        this.calculatedValuesByNodeKey[nodeKey] = {
          values: { ...(result.values || {}) },
          formattedValues: { ...(result.formattedValues || {}) }
        };

        this.applyLocalStateToCurrentRecord();
      }
    );
  }

  calculateCurrentGrid(): void {
    if (!this.vm || !this.isGridKind(this.vm.content?.kind)) {
      return;
    }

    this.service.calculateGrid(
      this.clientId,
      this.schemaName,
      this.tableName,
      this.recordId,
      {
        dataSetId: this.dataSetId,
        selectedNodeKey: this.vm.selectedNodeKey
      },
      (result: any) => {
        const nodeKey = result.nodeKey || this.vm!.selectedNodeKey;
        const merged: Record<string, CalculatedRecordState> = {
          ...(this.calculatedGridValuesByNodeKey[nodeKey] || {})
        };

        const rowValues = result.rowValues || {};
        const rowFormattedValues = result.rowFormattedValues || {};

        for (const recordId of Object.keys(rowValues)) {
          merged[recordId] = {
            values: { ...(rowValues[recordId] || {}) },
            formattedValues: { ...(rowFormattedValues[recordId] || {}) }
          };
        }

        this.calculatedGridValuesByNodeKey[nodeKey] = merged;
        this.applyLocalStateToCurrentGrid();
      }
    );
  }

  private persistCurrentDraft(): void {
    if (!this.vm || !this.isRecordKind(this.vm.content?.kind)) {
      return;
    }

    const nodeKey = this.currentNodeKey();
    if (!nodeKey) {
      return;
    }

    const existingDraft = this.draftsByNodeKey[nodeKey];
    const currentServerValues = this.buildServerValuesFromVm();

    if (!existingDraft) {
      this.draftsByNodeKey[nodeKey] = {
        nodeKey,
        recordId: this.vm.rootRecordId,
        title: this.vm.content.record?.title,
        originalValues: { ...currentServerValues },
        editedValues: { ...this.editedValues }
      };
      return;
    }

    this.draftsByNodeKey[nodeKey] = {
      ...existingDraft,
      title: this.vm.content.record?.title ?? existingDraft.title,
      originalValues: { ...existingDraft.originalValues },
      editedValues: { ...this.editedValues }
    };
  }

  private getCurrentDraft(): RecordDraftState | undefined {
    const nodeKey = this.currentNodeKey();
    if (!nodeKey) return undefined;
    return this.draftsByNodeKey[nodeKey];
  }

  private isDraftDirty(draft: RecordDraftState): boolean {
    const allKeys = new Set([
      ...Object.keys(draft.originalValues),
      ...Object.keys(draft.editedValues)
    ]);

    for (const key of allKeys) {
      if (draft.originalValues[key] !== draft.editedValues[key]) {
        return true;
      }
    }

    return false;
  }

  getDirtyDrafts(): RecordDraftState[] {
    return Object.values(this.draftsByNodeKey).filter(d => this.isDraftDirty(d));
  }

  hasAnyUnsavedChanges(): boolean {
    return this.getDirtyDrafts().length > 0;
  }

  dirtyRecordCount(): number {
    return this.getDirtyDrafts().length;
  }

  isDirty(fieldName?: string): boolean {
    if (!fieldName) return false;
    return this.originalValues[fieldName] !== this.editedValues[fieldName];
  }

  hasAnyDirty(): boolean {
    return Object.keys(this.editedValues).some(k => this.isDirty(k));
  }

  dirtyCount(): number {
    return Object.keys(this.editedValues).filter(k => this.isDirty(k)).length;
  }

  onFieldChange(field: RecordFieldVm, value: any): void {
    this.editedValues[field.fieldName] = value;
    this.persistCurrentDraft();
  }

  toggleEditMode(on: boolean): void {
    this.persistCurrentDraft();
    this.editMode = on;
    this.load();
  }

  discardCurrentDraft(): void {
    const draft = this.getCurrentDraft();
    if (!draft) return;

    draft.editedValues = { ...draft.originalValues };
    this.originalValues = { ...draft.originalValues };
    this.editedValues = { ...draft.editedValues };
  }

  discardAllDrafts(): void {
    for (const draft of Object.values(this.draftsByNodeKey)) {
      draft.editedValues = { ...draft.originalValues };
    }

    const current = this.getCurrentDraft();
    if (current) {
      this.originalValues = { ...current.originalValues };
      this.editedValues = { ...current.editedValues };
    }
  }

  save(): void {
    if (!this.vm) return;

    const nodeKey = this.vm.selectedNodeKey;
    const changedValues: Record<string, any> = {};

    for (const key of Object.keys(this.editedValues)) {
      if (this.originalValues[key] !== this.editedValues[key]) {
        changedValues[key] = this.editedValues[key];
      }
    }

    if (Object.keys(changedValues).length === 0) {
      console.log('Nothing to save');
      return;
    }

    const payload = {
      dataSetId: this.dataSetId,
      selectedNodeKey: nodeKey,
      values: changedValues
    };

    console.log('Saving draft', payload);

    this.service.save(
      this.clientId,
      this.schemaName,
      this.tableName,
      this.recordId,
      payload,
      (result: any) => {
        console.log('Save response', result);

        const nodeDraft = this.draftsByNodeKey[nodeKey];
        if (nodeDraft) {
          nodeDraft.originalValues = { ...nodeDraft.editedValues };
        }

        this.originalValues = { ...this.editedValues };
        this.editMode = false;
        this.load();
      }
    );
  }

  saveAllDrafts(): void {
    const dirtyDrafts = this.getDirtyDrafts();

    if (dirtyDrafts.length === 0) {
      console.log('No dirty drafts to save');
      return;
    }

    console.log('Save all drafts requested', dirtyDrafts);

    // Placeholder only for now.
    // Later, either:
    // 1) loop and call save endpoint per node, or
    // 2) build a batch save endpoint.
  }

  onToolbar(btn: ActionButtonVm): void {
    if (!btn.enabled) return;

    // First, handle section-toggle buttons
    if (btn.key === 'attachments' || btn.key === 'otherSection1' || btn.key === 'otherSection2') {
      // Toggle: if already active, hide; else show
      this.activeSection = this.activeSection === btn.key ? null : btn.key;
      return; // stop here, no other action needed
    }

    switch (btn.key) {
      case 'refresh':
        this.persistCurrentDraft();
        this.load();
        return;

      case 'edit':
        this.toggleEditMode(true);
        return;

      case 'cancel':
        this.editMode = false;
        this.discardCurrentDraft();
        this.load();
        return;

      case 'save':
        this.save();
        return;

      case 'calculate-record':
        this.calculateCurrentRecord();
        return;

      case 'calculate-grid':
        this.calculateCurrentGrid();
        return;

      default:
        console.log('Toolbar action:', btn.key);
        return;
    }
  }

  editorKind(field: RecordFieldVm): string {
    return field.editorKind || 'text';
  }

  selectNode(node: TreeNodeVm, event?: MouseEvent): void {
    event?.stopPropagation();
    this.persistCurrentDraft();
    this.page = 1;
    this.editMode = false;
    this.load(node.key);

    // Log after load
    setTimeout(() => {
      if (this.vm?.content?.record) {
        console.log('Selected Record:', this.vm.content.record);
      } else {
        console.log('No record available for this node');
      }
    }, 500); 
  }

  navigateBreadcrumb(nodeKey?: string | null): void {
    if (!nodeKey) return;
    this.persistCurrentDraft();
    this.page = 1;
    this.editMode = false;
    this.load(nodeKey);
  }

  openGridRow(row: GridRowVm): void {
    this.persistCurrentDraft();
    this.page = 1;
    this.editMode = false;
    this.load(row.navigateNodeKey);
  }

  runSearch(value: string): void {
    this.persistCurrentDraft();
    this.search = value ?? '';
    this.page = 1;
    this.load();
  }


  clearSearch(): void {
    this.persistCurrentDraft();
    this.search = '';
    this.page = 1;
    this.load();
  }

  sortBy(col: GridColumnVm): void {
    if (!col.sortable) return;
    this.persistCurrentDraft();

    if (this.sortField === col.fieldName) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = col.fieldName;
      this.sortDirection = 'asc';
    }

    this.page = 1;
    this.load();
  }

  goToPage(page: number): void {
    if (!this.vm?.content?.grid) return;
    const totalPages = this.totalPages(this.vm.content.grid);
    if (page < 1 || page > totalPages) return;

    this.persistCurrentDraft();
    this.page = page;
    this.load();
  }

  onPageSizeChange(value: string): void {
    const parsed = Number(value);
    this.persistCurrentDraft();
    this.pageSize = !isNaN(parsed) && parsed > 0 ? parsed : 25;
    this.page = 1;
    this.load();
  }

  totalPages(grid: GridContentVm): number {
    return Math.max(1, Math.ceil((grid.totalRows || 0) / Math.max(1, grid.pageSize || 25)));
  }

  isRecordKind(kind: any): boolean {
    return kind === 1 || kind === '1' || kind === 'Record';
  }

  isGridKind(kind: any): boolean {
    return kind === 2 || kind === '2' || kind === 'Grid';
  }

  toggleAttachments() {
    this.activeTab = this.activeTab === 'attachments' ? null : 'attachments';
  }

  trackByNode = (_: number, node: TreeNodeVm) => node.key;
  trackByField = (_: number, field: any) => field.fieldName;
  trackByColumn = (_: number, col: any) => col.fieldName;
  trackByRow = (_: number, row: GridRowVm) => row.id;
}
